function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

// Suppress extension message errors
window.addEventListener('error', (event) => {
  if (event.message?.includes('message channel closed')) {
    event.preventDefault();
  }
}, true);

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = [], checkDiff = false) {
    const oldHeight = oldNode.offsetHeight;
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    if (checkDiff) {
      requestAnimationFrame(() => {
        const newHeight = newNode.offsetHeight;
        const diff = newHeight - oldHeight;
        window._suppressHeaderReveal = true;
        window.scrollBy(0, diff);
        setTimeout(() => { window._suppressHeaderReveal = false; }, 100);
      });
    }

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    var summaryElement = event.currentTarget;
    var detailsElement = summaryElement.parentNode;
    if(summaryElement.dataset?.closeMenu){
      detailsElement = detailsElement.parentNode;
      summaryElement = detailsElement?.querySelector(':scope > summary');
    }
    summaryElement.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);

    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      if (value > max) {
        this.input.value = max;
      }
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}


/*
 * Deposit Sync
 *
 */
const DepositSettings = (() => {
  let cache = null;

  function readFromDOM() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return null;

    return {
      depositEnabled: drawer.dataset.depositEnabled === 'true',
      depositSkipIfFree: drawer.dataset.depositSkipIfFree === 'true',
      useStorefrontApi: drawer.dataset.useStorefrontApi === 'true'
    };
  }

  return {
    get() {
      if (cache) return cache;
      cache = readFromDOM();
      return cache;
    },
    clear() {
      cache = null; // Wenn der Drawer neu gerendert wird
    }
  };
})();

function createDepositSyncInstructions(cartData, {
  depositEnabled,
  depositSkipIfFree,
}) {
  const result = { updates: {}, additions: [] };

  const parsedState = cartData;
  if (!parsedState || !Array.isArray(parsedState.items) || parsedState.items.length === 0) {
    return result;
  }

  // Wenn Deposit generell nicht aktiv ist: nichts tun
  if (!depositEnabled) {
    return result;
  }

  // 1) Gehört strukturell zur Pfand-Gruppe? (kann Anker sein, auch wenn gratis)
  //    Die aufgelöste Pfand-Variante wird pro Variante über _deposit_variant getragen.
  const isPotentialParent = (item) => {
    if (item.parent_relationship) return false;
    // Eine Pfand-Line kann niemals selbst Parent sein — sonst würde eine gestrandete
    // Pfand-Line (z. B. mit backfilled Properties) ein eigenes Pfand angehängt bekommen.
    if (item.properties?._is_deposit === 'true') return false;
    if (String(item.variant_id) === String(item.properties?._deposit_variant)) return false;
    const unitsPerItem = parseInt(item.properties?.['_units_per_item'], 10);
    if (isNaN(unitsPerItem) || unitsPerItem <= 0) return false;
    if (!item.properties?._group) return false;
    if (!item.properties?._deposit_variant) return false;
    return true;
  };

  // Erkennt eine Pfand-Line ausschließlich über die Property (nicht über die Variant-ID),
  // damit DE (DPG) und AT (EWP) durch dieselbe Logik laufen.
  const isDepositLine = (item) => item.properties?._is_deposit === 'true';

  // 2) Zählt für die Pfand-Mengenberechnung? (Gratis ggf. ausgeschlossen)
  const countsForDeposit = (item) => {
    if (!isPotentialParent(item)) return false;
    if (depositSkipIfFree && item.final_line_price === 0) return false;
    return true;
  };

  // Parents per Key indexieren (nur strukturell valide Parents)
  const parentsByKey = {};
  for (const item of parsedState.items) {
    if (!item.parent_relationship && isPotentialParent(item)) {
      parentsByKey[item.key] = item;
    }
  }

  const parentsByGroup = {};
  const unitsPerItemByGroup = {};
  const depositVariantByGroup = {}; // aufgelöste Pfand-Variant-ID pro Gruppe
  const childrenByGroup = {};
  const depositBaseQtyByGroup = {}; // Basis-Menge für Pfand (ohne Gratis, falls skip_if_free)
  const updates = {};
  const additions = [];

  // --- Pass 0: Selbst-referenzierende Pfand-Lines aufräumen ---
  // Eine Line, deren variant_id auf ihr eigenes _deposit_variant zeigt, aber kein
  // _is_deposit trägt, ist ein gestrandetes Artefakt (z. B. Pfand-Variante mit
  // fälschlich gesetzten mn_deposit-Metafeldern + Backfill). Sie ist weder Parent
  // noch Pfand-Line und würde sonst für immer im Cart bleiben -> auf 0 setzen.
  for (const item of parsedState.items) {
    if (isDepositLine(item)) continue;
    if (!item.properties?._deposit_variant) continue;
    if (String(item.variant_id) !== String(item.properties._deposit_variant)) continue;
    if (item.quantity !== 0) {
      updates[item.key] = 0;
    }
  }

  // --- Pass 1: Parents einsammeln (pro VariantID + _group + Pfand-Variante) ---
  for (const item of parsedState.items) {
    if (!isPotentialParent(item)) continue;

    const grp = item.properties._group;
    const depVar = String(item.properties._deposit_variant);
    const groupKey = `${item.variant_id}::${grp}::${depVar}`;

    if (!parentsByGroup[groupKey]) parentsByGroup[groupKey] = [];
    parentsByGroup[groupKey].push(item);

    const unitsPerItem = parseInt(item.properties._units_per_item, 10) || 0;
    if (unitsPerItem > 0) {
      unitsPerItemByGroup[groupKey] = unitsPerItem;
    }

    depositVariantByGroup[groupKey] = depVar;

    // Nur Lines, die wirklich Pfand auslösen, zählen in die Basis-Menge
    if (countsForDeposit(item)) {
      depositBaseQtyByGroup[groupKey] =
        (depositBaseQtyByGroup[groupKey] || 0) + item.quantity;
    }
  }

  // --- Pass 2: Children einsammeln (Pfand-Items, erkannt über _is_deposit) ---
  for (const item of parsedState.items) {
    if (!isDepositLine(item)) continue; // nur Pfand-Lines

    const parentKey = item.parent_relationship?.parent_key;
    const parent = parentsByKey[parentKey];
    const grp = item.properties?._group || parent?.properties?._group;
    // Die Pfand-Line trägt die Pfand-Variante als eigene variant_id.
    const depVar = String(item.variant_id);

    // Ungültig/Waisen -> auf 0 setzen
    if (!parentKey || !parent || !grp) {
      if (item.quantity !== 0) {
        updates[item.key] = 0;
      }
      continue;
    }

    const groupKey = `${parent.variant_id}::${grp}::${depVar}`;

    // Kein passender Parent-Gruppe (z. B. falsche Pfand-Variante) -> Orphan -> 0
    if (!parentsByGroup[groupKey]) {
      if (item.quantity !== 0) {
        updates[item.key] = 0;
      }
      continue;
    }

    if (!childrenByGroup[groupKey]) childrenByGroup[groupKey] = [];
    childrenByGroup[groupKey].push(item);
  }

  // --- Pass 3: Pro Gruppe Sollmenge berechnen & Updates/Additions erzeugen ---
  for (const [groupKey, parents] of Object.entries(parentsByGroup)) {
    const upi = unitsPerItemByGroup[groupKey] || 0;
    if (upi <= 0) continue;

    // Nur Menge, die Pfand zahlen soll (Gratis ggf. rausgerechnet)
    const baseQty = depositBaseQtyByGroup[groupKey] || 0;
    const expectedQty = baseQty * upi;

    const depVar = depositVariantByGroup[groupKey];
    const depositVariantId = depVar ? parseInt(depVar, 10) : null;

    const kids = childrenByGroup[groupKey] || [];

    if (kids.length === 0) {
      // Kein Pfand-Child vorhanden -> ggf. eines hinzufügen
      const anchor = parents[0]; // immer das erste Item der Gruppe, auch wenn gratis
      const grp = anchor.properties._group;

      if (depositVariantId && expectedQty > 0 && anchor) {
        additions.push({
          quantity: expectedQty,
          group: grp,
          parentVariantId: anchor.variant_id,
          anchorParentKey: anchor.key,
          depositVariantId,
        });
      }
    } else if (kids.length === 1) {
      const child = kids[0];
      if (child.quantity !== expectedQty) {
        updates[child.key] = expectedQty;
      }
    } else {
      // Mehrere Pfand-Lines für dieselbe Gruppe -> konsolidieren
      let first = true;
      for (const child of kids) {
        if (first) {
          if (child.quantity !== expectedQty) {
            updates[child.key] = expectedQty;
          }
          first = false;
        } else if (child.quantity !== 0) {
          updates[child.key] = 0;
        }
      }
    }
  }

  result.updates = updates;
  result.additions = additions;
  return result;
}

async function depositSync(fallbackCartData) {
  try {
    const {
      depositEnabled,
      depositSkipIfFree,
      useStorefrontApi
    } = DepositSettings.get();

    if (!depositEnabled || typeof window.createDepositSyncInstructions !== 'function') {
      return fallbackCartData || null;
    }

    // Entscheiden, ob wir /cart.js laden müssen
    let ajaxCart = fallbackCartData;

    const isFullAjaxCart =
      ajaxCart &&
      Array.isArray(ajaxCart.items) && 
      typeof ajaxCart.total_price === 'number';

    if (!isFullAjaxCart) {
      // add.js-Response (item oder items ohne total_price) oder etwas anderes -> richtigen Cart laden
      ajaxCart = await fetch(`${routes.cart_url}.js`).then(r => r.json());
    }

    // Plan berechnen
    const { updates, additions } = createDepositSyncInstructions(ajaxCart, {
      depositEnabled,
      depositSkipIfFree,
    });

    const hasUpdates = updates && Object.keys(updates).length > 0;
    const hasAdditions = additions && additions.length > 0;

    if (!hasUpdates && !hasAdditions) {
      // Nichts zu tun -> den (jetzt sicheren) ajaxCart zurückgeben
      return fallbackCartData || ajaxCart;
    }

    let latestCart = ajaxCart;

    // Updates anwenden
    if (hasUpdates) {
      if (useStorefrontApi) {
        // Storefront API Weg
        const res = await co2.updateDepositQuantities(updates, ajaxCart);

        if (!res.ok) {
          console.warn('[depositSync] updateDepositQuantities nicht ok:', res);
          // latestCart bleibt ajaxCart
        } else {
          // Nach erfolgreichem SF-API-Update: /cart.js neu laden
          try {
            latestCart = await fetch(`${routes.cart_url}.js`).then(r => r.json());
          } catch (e) {
            console.warn('[depositSync] Konnte Cart nach SF-Updates nicht neu laden - nutze ajaxCart weiter', e);
            latestCart = ajaxCart;
          }
        }
      } else {
        // Klassischer AJAX-Weg
        const res = await fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({ updates }),
        });

        try {
          const json = await res.json();
          if (json && Array.isArray(json.items)) {
            latestCart = json;
          } else {
            console.warn('[depositSync] cart_update Response ohne items - nutze ajaxCart weiter');
            latestCart = ajaxCart;
          }
        } catch (e) {
          console.warn('[depositSync] Konnte cart_update-Response nicht parsen - nutze ajaxCart weiter', e);
          latestCart = ajaxCart;
        }
      }
    }

    // Additions
    if (hasAdditions) {
      const itemsForIndex = latestCart.items || [];

      // Für die SF-API brauchen wir das Mapping per Index
      const resolvedAdditions = additions
        .map(add => {
          const idx = itemsForIndex.findIndex(i => i.key === add.anchorParentKey);
          return { ...add, anchorAjaxIndex: idx };
        })
        .filter(add => add.anchorAjaxIndex >= 0);

      if (useStorefrontApi) {
        // Storefront API: cartLinesAdd mit Parent-Verknüpfung.
        // Jede Addition trägt ihre je Eintrag aufgelöste Pfand-Variant-ID.
        if (resolvedAdditions.length > 0) {
          const res = await co2.addDepositItems(resolvedAdditions);
          if (!res.ok) {
            console.warn('[depositSync] addDepositItems nicht ok:', res);
          }
        }

        // Nach Additions: Cart neu laden
        try {
          latestCart = await fetch(`${routes.cart_url}.js`).then(r => r.json());
        } catch (e) {
          console.warn('[depositSync] Konnte Cart nach Additions nicht neu laden - nutze latestCart weiter', e);
        }
      } else {
        // AJAX-Variante: cart/add.js mit parent_line_key & Properties wie früher
        try {
          const ajaxItems = additions.map(add => ({
            id: add.depositVariantId,
            quantity: add.quantity,
            parent_line_key: add.anchorParentKey,
            properties: {
              _group: add.group,
              _is_deposit: 'true',
            },
          }));

          await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: ajaxItems }),
          });

          // Danach: vollen Cart neu laden, damit latestCart sicher ein Cart ist
          latestCart = await fetch(`${routes.cart_url}.js`).then(r => r.json());
        } catch (e) {
          console.error('[depositSync] Additions per AJAX fehlgeschlagen', e);
          // latestCart bleibt wie zuvor
        }
      }
    }

    return latestCart;
  } catch (err) {
    console.error('[depositSync] Fehler:', err);
    return fallbackCartData || null;
  }
}

function collectMissingDepositProperties(root = document) {
  const tiles = root.querySelectorAll(
    '[data-needs-deposit-properties="true"][data-item-key]'
  );

  const updates = [];

  for (const tile of tiles) {
    const key = tile.dataset.itemKey;
    if (!key) continue;

    let properties = {};

    if (tile.dataset.itemProperties) {
      try {
        const parsed = JSON.parse(tile.dataset.itemProperties);

        if (Array.isArray(parsed)) {
          // Shopify: [["key","value"], ...] → in Objekt umwandeln
          for (const [k, v] of parsed) {
            if (k != null) properties[k] = v;
          }
        } else if (parsed && typeof parsed === 'object') {
          properties = parsed;
        }
      } catch {
        properties = {};
      }
    }

    const upi = tile.dataset.depositVariantUpi;
    const group = tile.dataset.depositGroup;
    const depositVariant = tile.dataset.depositVariant;
    const isDeposit = tile.dataset.isDeposit;

    let hasChange = false;

    if (upi !== undefined && properties._units_per_item == null) {
      properties._units_per_item = upi;
      hasChange = true;
    }

    if (group !== undefined && properties._group == null) {
      properties._group = group;
      hasChange = true;
    }

    // Parent-Line: aufgelöste Pfand-Variante nachtragen
    if (depositVariant !== undefined && properties._deposit_variant == null) {
      properties._deposit_variant = depositVariant;
      hasChange = true;
    }

    // Pfand-Line: _is_deposit nachtragen
    if (isDeposit !== undefined && properties._is_deposit == null) {
      properties._is_deposit = isDeposit;
      hasChange = true;
    }

    if (!hasChange) continue;

    updates.push({
      key,
      properties,
    });
  }

  return updates;
}

async function adjustDepositProperties(root = document) {
  // 1) Fehlende Deposit-Properties im DOM einsammeln
  const updates = collectMissingDepositProperties(root);

  // Nichts zu tun -> direkt raus
  if (!updates.length) {
    return null;
  }

  const { useStorefrontApi } = DepositSettings.get() || {};

  // 3) Storefront-Variante
  if (useStorefrontApi) {
    // Aktuellen Ajax-Cart laden (für Mapping key -> ajaxIndex)
    const cartRes = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    const ajaxCart = await cartRes.json();
    const items = ajaxCart.items || [];

    // Updates mit ajaxIndex anreichern. Key-Fallback wie im AJAX-Zweig: veraltete
    // Markup-Keys (Cart-Attribut-Update nach dem Rendern vergibt neue Keys) über
    // die variant_id auf eine Line auflösen, der die Properties noch fehlen.
    const updatesWithIndex = updates
      .map((u) => {
        let idx = items.findIndex((i) => i.key === u.key);
        if (idx === -1) {
          const variantId = String(u.key).split(':')[0];
          idx = items.findIndex(
            (i) =>
              String(i.variant_id) === variantId &&
              !i.parent_relationship &&
              i.properties?._is_deposit !== 'true' &&
              Object.keys(u.properties).some((k) => i.properties?.[k] == null)
          );
        }
        if (idx === -1) return { ajaxIndex: -1, properties: u.properties };
        // Properties der aktuellen Line haben Vorrang; Markup füllt nur Lücken.
        const merged = Object.assign({}, u.properties, items[idx].properties || {});
        return { ajaxIndex: idx, properties: merged };
      })
      .filter((u) => u.ajaxIndex >= 0);

    if (updatesWithIndex.length) {
      const result = await co2.updateDepositProperties(updatesWithIndex);
      if (!result || !result.ok) {
        console.warn('[adjustDepositProperties] updateDepositProperties nicht ok:', result);
      }
    }

    // Nach Storefront-Update den Cart neu laden
    const finalRes = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    const finalCart = await finalRes.json();

    return finalCart;
  }

  // 4) AJAX-Variante: /cart/change.js sequentiell nach Position (line)
  let lastCart = null;

  // Cart einmal laden und Positionen ermitteln
  try {
    const cartRes = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!cartRes.ok) {
      console.warn(
        '[adjustDepositProperties] Konnte initialen Cart nicht laden:',
        cartRes.status
      );
    } else {
      lastCart = await cartRes.json();
    }
  } catch (err) {
    console.warn('[adjustDepositProperties] Fehler beim Laden des initialen Carts:', err);
  }

  // WICHTIG: Die Line-Position wird pro Update FRISCH aus dem jeweils letzten
  // Cart-Stand aufgelöst. Ein Property-Update kann die Line mit einer bereits
  // vorhandenen identischen Line MERGEN (Shopify entfernt dann eine Line und
  // alle folgenden Positionen verschieben sich). Mit vorab berechneten Positionen
  // trifft das nächste Update sonst die falsche Line (beobachtet: Parent-Properties
  // + Menge landeten auf einer Pfand-Line).
  //
  // Die Keys aus dem Markup können zusätzlich VERALTET sein: Schreibt der
  // Code-Link nach dem Rendern ein Cart-Attribut (neuer Discount-Code), vergibt
  // Shopify neue Item-Keys, und data-item-key passt nicht mehr. Fallback dann:
  // Line über die variant_id (Key-Präfix) auflösen — aber nur auf eine Line, der
  // die nachzutragenden Properties tatsächlich noch fehlen (nie Pfand-Lines,
  // nie genestete Lines).
  const resolveLineIndex = (items, key, properties) => {
    const byKey = items.findIndex((item) => item.key === key);
    if (byKey !== -1) return byKey;

    const variantId = String(key).split(':')[0];
    return items.findIndex(
      (item) =>
        String(item.variant_id) === variantId &&
        !item.parent_relationship &&
        item.properties?._is_deposit !== 'true' &&
        Object.keys(properties).some((k) => item.properties?.[k] == null)
    );
  };

  for (const { key, properties } of updates) {
    const items = (lastCart && lastCart.items) || [];
    const idx = resolveLineIndex(items, key, properties);

    if (idx === -1) {
      // Line existiert nicht mehr (z. B. durch Merge nach vorherigem Update) -> ok
      console.warn(
        '[adjustDepositProperties] Kein Line Item für key im aktuellen Cart gefunden, überspringe:',
        key
      );
      continue;
    }

    const item = items[idx];

    // Properties der aktuellen Line haben Vorrang; das Markup-Set füllt nur
    // die Lücken auf (das Markup kann durch die Key-Drift veraltet sein).
    const mergedProperties = Object.assign({}, properties, item.properties || {});

    const res = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        line: idx + 1, // Shopify line ist 1-basiert, frisch aufgelöst
        quantity: item.quantity,
        properties: mergedProperties,
      }),
    });

    if (!res.ok) {
      console.warn(
        '[adjustDepositProperties] /cart/change.js nicht ok für line:',
        idx + 1,
        res.status
      );
      continue;
    }

    // Response ist der aktualisierte Cart – Basis für die nächste Auflösung
    lastCart = await res.json();
  }

  if (!lastCart) {
    // Fallback: Cart neu laden, falls alles schiefging
    const res = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    lastCart = await res.json();
  }

  return lastCart;
}


async function runInitialDepositSync(root = document) {
  // 1) Deposit Properties ergänzen (falls nötig)
  const adjustedCart = await adjustDepositProperties(root);

  // -> Wenn adjustDepositProperties nichts tun musste:
  if (!adjustedCart) {
    // Keine Änderungen -> Kein depositSync!
    return null;
  }

  // -> Wenn Properties ergänzt wurden -> depositSync
  const cartData = await depositSync(adjustedCart);

  // Event feuern
  publish(PUB_SUB_EVENTS.cartUpdate, { source: 'global', cartData });

  return cartData;
}

window.addEventListener('DOMContentLoaded', () => {
  runInitialDepositSync(document);
});



/*
 * Purchase Limit Validation
 *
 */
// Wird von Alpine via validateCartLimits($event) aufgerufen
window.validateCartLimits = function (event) {
  // Falls z. B. Gift-Reminder bereits blockiert hat:
  if (event.defaultPrevented) return;

  const form = document.getElementById('CartDrawer-Form');
  if (!form) return;

  const result = runCartLimitChecks(form);

  if (!result.ok) {
    event.preventDefault();
    showCartDrawerError(result.messages);
  }
};

function showCartDrawerError(messages) {
  const el = document.getElementById('CartDrawer-ErrorMessage');
  if (!el) return;

  // Mehrere Fehlermeldungen untereinander ausgeben
  el.innerHTML = messages.map(msg => `<p>${msg}</p>`).join('');

  // Anzeige aktivieren
  el.style.display = '';
}


/**
 * Hauptfunktion: prüft Varianten- und Gruppen-Limits
 */
function runCartLimitChecks(rootEl) {
  const variantTiles = rootEl.querySelectorAll('[data-limit-variant="true"]');
  const productTiles = rootEl.querySelectorAll('[data-limit-product="true"]');

  const i18n = loadPurchaseLimitI18n();

  // Wichtig: Aktive Sprache des Shops
  const locale = Shopify?.locale || 'de';

  const variantErrors = [];
  const groupErrors = [];

  //
  // 1) Varianten-Limits
  //
  if (variantTiles.length > 0) {
    const variantTotals = collectVariantTotals(variantTiles);

    for (const [variantId, info] of variantTotals) {
      if (info.hasLimit && info.totalQty > info.maxQty) {
        const tpl = i18n.variant_limit_exceeded;
        const msg = formatTemplate(tpl, {
          product_title: info.productTitle || '',
          variant_title: info.variantTitle || '',
          limit: info.maxQty,
          quantity: info.totalQty,
        });
        variantErrors.push(msg);
      }
    }
  }

  //
  // 2) Produkt-/Gruppen-Limits
  //
  if (productTiles.length > 0) {
    const configEl = document.getElementById('purchase-limit-config');
    let config = {};

    if (configEl) {
      try {
        config = JSON.parse(configEl.textContent.trim());
      } catch (e) {
        console.error('Ungültiges purchase-limit-config JSON:', e);
      }
    }

    if (config && Object.keys(config).length > 0) {
      const productTotals = collectProductTotals(productTiles, config);

      for (const [productId, totals] of productTotals) {
        const productConfig = config[productId];
        if (!productConfig || !Array.isArray(productConfig.groups)) continue;

        for (const group of productConfig.groups) {
          const limit = Number(group.quantity) || 0;
          const option = group.option;

          // Gruppennamen lokalisieren
          const groupName =
            (group.name && (
              group.name[locale] ||
              group.name.en ||
              group.name.de
            )) || '';

          const productTitle = totals.productTitle || '';

          let actual = 0;
          if (option === 'product') {
            actual = totals.totalQty;
          } else {
            actual = totals.optionQty.get(option) || 0;
          }

          if (actual > limit) {
            if (option === 'product') {
              const tpl = i18n.product_limit_exceeded;
              const msg = formatTemplate(tpl, {
                product_title: productTitle,
                limit,
                quantity: actual,
              });
              groupErrors.push(msg);
            } else {
              const tpl = i18n.variant_group_exceeded;
              const msg = formatTemplate(tpl, {
                product_title: productTitle,
                group_name: groupName,
                limit,
                quantity: actual,
              });
              groupErrors.push(msg);
            }
          }
        }
      }
    }
  }

  return {
    ok: variantErrors.length === 0 && groupErrors.length === 0,
    messages: [...variantErrors, ...groupErrors],
  };
}



/**
 * Variantenmengen + Titel aggregieren
 */
function collectVariantTotals(nodeList) {
  const map = new Map();

  for (const tile of nodeList) {
    const ds = tile.dataset;
    const variantId = ds.variantId;
    const qty = Number(ds.currentQuantity) || 0;

    const hasLimit = ds.limitVariant === 'true';
    const maxQty = hasLimit ? Number(ds.variantMaxQuantity || 0) : 0;

    if (!variantId) continue;

    if (!map.has(variantId)) {
      map.set(variantId, {
        totalQty: 0,
        hasLimit: false,
        maxQty: 0,
        productTitle: ds.productTitle || '',
        variantTitle: ds.variantTitle || '',
      });
    }

    const info = map.get(variantId);
    info.totalQty += qty;

    // Produkt-/Variantentitel nur beim ersten Vorkommen setzen
    if (!info.productTitle && ds.productTitle) {
      info.productTitle = ds.productTitle;
    }
    if (!info.variantTitle && ds.variantTitle) {
      info.variantTitle = ds.variantTitle;
    }

    if (hasLimit) {
      info.hasLimit = true;
      if (maxQty > info.maxQty) info.maxQty = maxQty;
    }
  }

  return map;
}


/**
 * Produktmengen + Option-Mengen + Produkttitel aggregieren
 */
function collectProductTotals(nodeList, config) {
  const map = new Map();

  for (const tile of nodeList) {
    const ds = tile.dataset;
    const productId = ds.productId;
    const qty = Number(ds.currentQuantity) || 0;
    const optionId = ds.sizeOptionValueId || '';

    if (!productId || !config[productId]) continue;

    if (!map.has(productId)) {
      map.set(productId, {
        totalQty: 0,
        optionQty: new Map(),
        productTitle: ds.productTitle || '',
      });
    }

    const info = map.get(productId);
    info.totalQty += qty;

    if (!info.productTitle && ds.productTitle) {
      info.productTitle = ds.productTitle;
    }

    if (optionId) {
      const prev = info.optionQty.get(optionId) || 0;
      info.optionQty.set(optionId, prev + qty);
    }
  }

  return map;
}


/**
 * Locale-Strings aus dem Script-Tag laden
 */
function loadPurchaseLimitI18n() {
  const el = document.getElementById('purchase-limit-i18n');
  if (!el) return {};
  try {
    return JSON.parse(el.textContent.trim());
  } catch (e) {
    console.error('purchase-limit-i18n JSON ungültig:', e);
    return {};
  }
}


/**
 * Platzhalter-Helper:
 * template: "Du kannst maximal %limit% Stück von %product_title% bestellen."
 * replacements: { limit: 3, product_title: "XYZ" }
 */
function formatTemplate(template, replacements) {
  let result = template || '';
  for (const [key, value] of Object.entries(replacements)) {
    const token = '%' + key + '%';
    result = result.split(token).join(String(value));
  }
  return result;
}




/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    var summaryElement = event.currentTarget;
    var detailsElement = summaryElement.parentNode;
    if (summaryElement.dataset?.closeMenu){
      detailsElement = detailsElement.parentNode;
      summaryElement = detailsElement?.querySelector(':scope > summary');
    }
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    if (window.innerWidth < 1280) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(this.preloadAllMenuImages.bind(this));
      } else {
        this.preloadAllMenuImages().bind(this);
      }
    }
  }

  preloadAllMenuImages() {
    const menuImages = this.querySelectorAll('[data-image="preload"]');

    menuImages.forEach(imageElement => {
      const img = new Image();
      img.src = imageElement.src;
    });
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }

    if (this.id === 'PopupModal-login') {
      this.loginRedirect();
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
    const appendTo = this.getAttribute('data-append') ?? false;
    if(appendTo) {
      this.closest(appendTo)?.appendChild(this);
    }else{
      document.body.appendChild(this);
    }
  }

  loginRedirect() {
    if (document.referrer !== window.location.href && window.location.href.includes('login')) {
      history.replaceState({ returnTo: 'login' }, '', document.referrer);
    }

    // open modal if form has errors
    if (history.state?.returnTo === 'login') {
      const popup = document.querySelector('[id="PopupModal-login"]');
      popup.setAttribute('open', '');
      history.replaceState({}, '', window.location.href);
    }
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    this.classList.add('modal-open');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    this.classList.remove('modal-open');
    setTimeout(() => {
      document.body.classList.remove('overflow-hidden');
      document.body.dispatchEvent(new CustomEvent('modalClosed', {
        detail: { id: this.id }
      }));
      this.removeAttribute('open');
      removeTrapFocus(this.openedBy);
      window.pauseAllMedia();
    }, 300);
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide?.classList.add(`${animationClassOut}-${direction}`);
    nextSlide?.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide?.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide?.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'))
      .map(({ dataset }) => dataset.optionValueId)
      .filter(Boolean);
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
  window.dispatchEvent(new CustomEvent('cart.update', { detail: { cart: event.cartData } }));
});

window.addEventListener( 'co2.afterCartAttributeChange', () => {
  publish(PUB_SUB_EVENTS.cartUpdate, { source: 'global', cartData: null })
});

customElements.define(
  'layer-dropdown',
  class LayerDropdown extends HTMLElement {
    constructor() {
      super();
      this.mql = window.matchMedia('(min-width: 750px)');
      this.elements = {
        button: this.querySelector('.disclosure__toggle-button'),
        panel: this.querySelector('.disclosure__list-wrapper'),
      };
      this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
      this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
      this.addEventListener('focusout', this.closeSelector.bind(this));
      this.elements.button.addEventListener('click', this.openSelector.bind(this));
    }

    hidePanel() {
      this.elements.button.setAttribute('aria-expanded', 'false');
      this.elements.panel.setAttribute('hidden', true);
    }

    onContainerKeyDown(event) {
      const focusableItems = Array.from(this.querySelectorAll('a')).filter(
        (item) => !item.parentElement.classList.contains('hidden')
      );
      let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
      let itemToFocus;

      switch (event.code.toUpperCase()) {
        case 'ARROWUP':
          event.preventDefault();
          itemToFocus =
            focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
          itemToFocus.focus();
          break;
        case 'ARROWDOWN':
          event.preventDefault();
          itemToFocus =
            focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
          itemToFocus.focus();
          break;
      }

      setTimeout(() => {
        focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        if (focusedItemIndex > -1) {
          this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
        } else {
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
      });
    }

    onContainerKeyUp(event) {
      event.preventDefault();

      switch (event.code.toUpperCase()) {
        case 'ESCAPE':
          if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
          this.hidePanel();
          event.stopPropagation();
          this.elements.button.focus();
          break;
        case 'SPACE':
          if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
          this.openSelector();
          break;
      }
    }

    openSelector() {
      this.elements.button.focus();
      this.elements.panel.toggleAttribute('hidden');
      this.elements.button.setAttribute(
        'aria-expanded',
        (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
      );
    }

    closeSelector(event) {
      if (
        !this.contains(event.target) ||
        !this.contains(event.relatedTarget)
      ) {
        this.hidePanel();
      }
    }
  }
);
